import { defineStore } from "pinia";
import { computed, ref } from "vue";

// api
import { userApi } from "@/api/user";

// interfaces
import type { User, UserFeatures } from "@/interfaces/User";
import { UserFeature as UserFeatureEnum } from "@/enums/UserFeature";

export const useUser = defineStore("user", () => {
  const getUserInitialData = (): User => ({
    id: "",
    name: "",
    surname: "",
    email: "",
    company: {
      id: 0,
      name: "",
      city: "",
      logo: "",
    },
    privileges: {
      is_job_offer_moderator: false,
      is_job_application_viewer: false,
      is_job_offer_importer: false,
      is_review_moderator: false,
      is_news_author: false,
      is_company_owner: false,
      is_profile_editor: false,
      is_gold_profile_editor: false,
      is_job_offer_template_editor: false,
      is_interview_moderator: false,
      is_interview_writer: false,
      is_company_services_admin: false,
      is_company_staff_manager: false,
      is_companies_admin: false,
      is_seo_tags_editor: false,
      is_candidate_profile_viewer: false,
    },
    job_offers_with_application_locks: [],
  });

  const getUserFeaturesInitialData = (): UserFeatures => ({
    [UserFeatureEnum.STANDARD_PLUS_JOB_OFFER_PACKAGE]: false,
    [UserFeatureEnum.PREMIUM_JOB_OFFER_PACKAGE]: false,
    [UserFeatureEnum.PRO_JOB_OFFER_PACKAGE]: false,
    [UserFeatureEnum.EXTENDED_PROFILE]: false,
    [UserFeatureEnum.JOB_AGENCY_JOB_OFFERS]: false,
    [UserFeatureEnum.OPEN_CANDIDATE_PROFILE_FEATURE]: false,
    [UserFeatureEnum.CANDIDATE_PROFILE_FEATURE]: false,
  });

  const user = ref<User>(getUserInitialData());
  const userFeatures = ref<UserFeatures>(getUserFeaturesInitialData());
  const isLoading = ref(false);

  const isUserLogged = computed(() => !!user.value.id);
  const companyName = computed(() => user.value.company.name);
  const companyCity = computed(() => user.value.company.city);
  const companyLogo = computed(() => user.value.company.logo);

  const fetchUserData = async ({ companyId }: { companyId: string }) => {
    try {
      isLoading.value = true;

      const [userResponseData, userFeaturesResponseData] = await Promise.all([
        userApi().getUser({ companyId }),
        userApi().getFeatures({ companyId }),
      ]);

      user.value = userResponseData;
      userFeatures.value = userFeaturesResponseData;
    } catch (error) {
      console.error(error);
    } finally {
      isLoading.value = false;
    }
  };

  const logout = async () => {
    try {
      isLoading.value = true;
      await userApi().logout();
      user.value = getUserInitialData();
    } catch (error) {
      console.error(error);
    } finally {
      isLoading.value = false;
    }
  };

  return {
    user,
    userFeatures,
    isLoading,
    isUserLogged,
    companyName,
    companyCity,
    companyLogo,
    fetchUserData,
    logout,
  };
});
